// react/require-default-props wants a defaultProps entry for every optional
// prop — but defaultProps on function components is deprecated as of React
// 18.3 (console warning, removed in 19), so this file uses plain JS default
// parameters instead, same as the rest of this codebase's common/ components
// (FilterButton, MarkdownEditor, etc.) and the same reasoning the project's
// own .eslintrc.json already uses to turn this rule off for src/components/ui.
/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'src/components/ui/button';
import { cn } from 'src/lib/utils';

const noop = () => {};

// A Button that starts fully expanded (icon + label), auto-collapses to an
// icon-only pill after `collapseDelay` ms, and smoothly re-expands on
// hover/keyboard focus. Built for header action rows that get crowded with
// several labeled buttons — not specific to any one page.
//
// The label's width animates via the same grid-template 0fr/1fr trick
// InterviewBrief.jsx's QuestionRating/QuestionCard components already use
// (there on grid-template-rows, here on grid-template-columns) — the one way
// to transition toward an unmeasured "auto" size without a JS layout
// measurement or ResizeObserver. The icon-to-label gap is owned by this
// component's own wrapper span (not Button's base `gap-[6px]`, which always
// applies between Button's direct children regardless of their width) so it
// can collapse to 0 in lockstep with the label, leaving a clean icon-only
// button at rest instead of a stray sliver of empty space.
//
// The label text stays in the DOM at all times (only its width/overflow are
// animated, never display/visibility), so it remains part of the button's
// accessible name for screen readers even while visually collapsed.
const AutoCollapseButton = React.forwardRef(
  (
    {
      icon: Icon,
      label,
      collapseDelay = 2000,
      className,
      variant = 'outline',
      size = 'headerSm',
      onMouseEnter = noop,
      onMouseLeave = noop,
      onFocus = noop,
      onBlur = noop,
      ...props
    },
    ref
  ) => {
    const [settled, setSettled] = useState(collapseDelay <= 0);
    const [hovered, setHovered] = useState(false);
    const expanded = !settled || hovered;

    useEffect(() => {
      if (collapseDelay <= 0) return undefined;
      const timer = setTimeout(() => setSettled(true), collapseDelay);
      return () => clearTimeout(timer);
    }, [collapseDelay]);

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={className}
        onMouseEnter={(e) => {
          onMouseEnter(e);
          setHovered(true);
        }}
        onMouseLeave={(e) => {
          onMouseLeave(e);
          setHovered(false);
        }}
        onFocus={(e) => {
          onFocus(e);
          setHovered(true);
        }}
        onBlur={(e) => {
          onBlur(e);
          setHovered(false);
        }}
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...props}
      >
        <span
          className={cn(
            'flex items-center transition-[gap] duration-300 ease-out motion-reduce:transition-none',
            expanded ? 'gap-1.5' : 'gap-0'
          )}
        >
          <Icon className="h-4 w-4 shrink-0" />
          <span
            className="grid transition-[grid-template-columns] duration-300 ease-out motion-reduce:transition-none"
            style={{ gridTemplateColumns: expanded ? '1fr' : '0fr' }}
          >
            <span className="overflow-hidden whitespace-nowrap">{label}</span>
          </span>
        </span>
      </Button>
    );
  }
);
AutoCollapseButton.displayName = 'AutoCollapseButton';

AutoCollapseButton.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.node.isRequired,
  collapseDelay: PropTypes.number,
  className: PropTypes.string,
  variant: PropTypes.string,
  size: PropTypes.string,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
};

export default AutoCollapseButton;
